import { DEFAULT_ADJUSTMENTS, DEFAULT_GOALS, newPolicy } from './defaults.js'
import { isLegalBeneficiary, normalizeDeathBenefitStatus } from './beneficiary.js'

export const MAX_BACKUP_SIZE = 5 * 1024 * 1024

const uniqueId = () => `pol_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`

export const normalizePolicy = (input = {}) => {
  const base = newPolicy()
  const policy = { ...base, ...input }
  policy.id = String(input.id || uniqueId())
  policy.deathBenefitStatus = normalizeDeathBenefitStatus(input.deathBenefitStatus)
  policy.beneficiaryReminderPaused = Boolean(input.beneficiaryReminderPaused)
  if (policy.deathBenefitStatus !== 'yes' || !isLegalBeneficiary(policy.beneficiary)) policy.beneficiaryReminderPaused = false
  policy.riders = Array.isArray(input.riders) ? input.riders.map((rider) => ({
    type: '医疗险', name: '', guaranteePeriod: '终身', premium: 0, coverage: 0,
    shareCoverage: false, shareWithId: '', ...rider,
  })) : []
  policy.shareCoverage = Boolean(policy.shareCoverage)
  policy.shareWithId = policy.shareCoverage ? String(policy.shareWithId || '') : ''
  policy.riders.forEach((rider) => {
    rider.shareCoverage = Boolean(rider.shareCoverage)
    rider.shareWithId = rider.shareCoverage ? String(rider.shareWithId || '') : ''
  })
  if (policy.lastPaidYear === undefined || policy.lastPaidYear === '' || policy.lastPaidYear === null) {
    const effectiveYear = Number(String(policy.effectiveDate || '').slice(0, 4))
    policy.lastPaidYear = effectiveYear || null
  }
  return policy
}

const normalizeAdjustments = (input = {}) => Object.fromEntries(Object.keys(DEFAULT_ADJUSTMENTS).map((key) => {
  const current = input[key] || {}
  return [key, {
    excludedIds: Array.isArray(current.excludedIds) ? [...new Set(current.excludedIds.map(String))] : [],
    customItems: Array.isArray(current.customItems) ? current.customItems.map((item) => ({
      id: String(item.id || `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`),
      name: String(item.name || '自定义保障'), amount: Number(item.amount) || 0,
    })) : [],
  }]
}))

export const validateBackupObject = (input) => {
  const errors = []
  const rawPolicies = Array.isArray(input) ? input : input?.policies
  if (!Array.isArray(rawPolicies)) errors.push('文件中缺少 policies 数组。')
  if (rawPolicies?.length > 5000) errors.push('单次导入最多支持 5000 份保单。')
  if (!Array.isArray(rawPolicies)) return errors
  rawPolicies.forEach((policy, index) => {
    if (!policy || typeof policy !== 'object' || Array.isArray(policy)) errors.push(`第 ${index + 1} 条保单格式错误。`)
  })
  return errors
}

export const parseBackupText = (text) => {
  if (new Blob([text]).size > MAX_BACKUP_SIZE) throw new Error('备份文件超过 5MB，请拆分后导入。')
  let parsed
  try { parsed = JSON.parse(text) } catch { throw new Error('备份不是有效的 JSON 文件。') }
  const errors = validateBackupObject(parsed)
  if (errors.length) throw new Error(errors.join('\n'))
  const rawPolicies = Array.isArray(parsed) ? parsed : parsed.policies
  return {
    version: Array.isArray(parsed) ? 'legacy' : String(parsed.version || '2.0'),
    policies: rawPolicies.map(normalizePolicy),
    gapGoals: { ...DEFAULT_GOALS, ...(Array.isArray(parsed) ? {} : parsed.gapGoals) },
    gapAdjustments: normalizeAdjustments(Array.isArray(parsed) ? {} : parsed.gapAdjustments),
  }
}

export const policyFingerprint = (policy) => [
  policy.productName, policy.company, policy.applicant, policy.insuredPerson, policy.effectiveDate, policy.beneficiary || '法定',
].map((value) => String(value || '').trim().toLowerCase()).join('|')

export const previewImport = (incoming, existing) => {
  const existingKeys = new Set(existing.map(policyFingerprint))
  const duplicateIndexes = []
  const invalidIndexes = []
  incoming.policies.forEach((policy, index) => {
    if (!policy.productName || !policy.insuredPerson || !policy.effectiveDate) invalidIndexes.push(index)
    if (existingKeys.has(policyFingerprint(policy))) duplicateIndexes.push(index)
  })
  return {
    total: incoming.policies.length,
    newCount: incoming.policies.length - duplicateIndexes.length,
    duplicateCount: duplicateIndexes.length,
    invalidCount: invalidIndexes.length,
    duplicateIndexes,
    invalidIndexes,
  }
}

const rebaseImportedIds = (policies, adjustments) => {
  const idMap = new Map(policies.map((policy) => [policy.id, uniqueId()]))
  const replaceId = (value) => {
    if (!value) return value
    for (const [oldId, newId] of idMap.entries()) if (String(value).startsWith(oldId)) return String(value).replace(oldId, newId)
    return value
  }
  const rebasedPolicies = policies.map((policy) => ({
    ...policy,
    id: idMap.get(policy.id),
    shareWithId: replaceId(policy.shareWithId),
    riders: policy.riders.map((rider) => ({ ...rider, shareWithId: replaceId(rider.shareWithId) })),
  }))
  const rebasedAdjustments = Object.fromEntries(Object.entries(adjustments).map(([key, value]) => [key, {
    excludedIds: value.excludedIds.map(replaceId),
    customItems: value.customItems.map((item) => ({ ...item, id: `${item.id}_${Math.random().toString(36).slice(2, 6)}` })),
  }]))
  return { policies: rebasedPolicies, adjustments: rebasedAdjustments }
}

export const applyImport = ({ incoming, existingPolicies, currentAdjustments, mode, skipDuplicates = true }) => {
  const preview = previewImport(incoming, existingPolicies)
  const selected = incoming.policies.filter((_, index) => !skipDuplicates || !preview.duplicateIndexes.includes(index))
  const rebased = rebaseImportedIds(selected, incoming.gapAdjustments)
  if (mode === 'overwrite') return {
    policies: rebased.policies,
    goals: incoming.gapGoals,
    adjustments: rebased.adjustments,
  }
  const adjustments = Object.fromEntries(Object.keys(DEFAULT_ADJUSTMENTS).map((key) => [key, {
    excludedIds: [...new Set([...(currentAdjustments[key]?.excludedIds || []), ...(rebased.adjustments[key]?.excludedIds || [])])],
    customItems: [...(currentAdjustments[key]?.customItems || []), ...(rebased.adjustments[key]?.customItems || [])],
  }]))
  return { policies: [...existingPolicies, ...rebased.policies], goals: incoming.gapGoals, adjustments }
}

export const createBackup = (policies, gapGoals, gapAdjustments) => ({
  version: '2.0', exportedAt: new Date().toISOString(), policies, gapGoals, gapAdjustments,
})
